import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, Icon, Modal } from "semantic-ui-react";

import { ROLES } from "models";
import {
  useDeletePairedUsersMutation,
  useDmsUsersQuery,
  useOneIPlanningMechanicsQuery,
  usePlanItMechanicsQuery,
  useUpdatePairDmsUsersMutation,
  useUpdatePairOneIUsersMutation,
  useUpdatePairPlanItUsersMutation
} from "modules/UsersDashboard/components/PairUsers/hooks";
import "modules/UsersDashboard/components/PairUsers/PairUsers.scss";
import { PAIR_USERS_OPTIONS } from "modules/UsersDashboard/components/UsersDashboardFilters";
import { useGetUsersDashboardFilters } from "modules/UsersDashboard/components/UsersDashboardFilters/hooks";
import { useUsersQuery } from "modules/UsersDashboard/hooks";

import type { DropdownItemProps } from "components/Dropdown";
import type { User } from "models";

type PairUsersProps = {
  pairType: PAIR_USERS_OPTIONS | null;
  onClose: () => void;
};

export type PairedUserState = {
  user_id: null | number;
  pair_to: null | number;
};

const getPairUsersTitle = (pairType: PAIR_USERS_OPTIONS | null) => {
  if (pairType === PAIR_USERS_OPTIONS.OneIAutomotive) return "1i Automotive";
  if (pairType === PAIR_USERS_OPTIONS.PlanIt) return "PlanIT";
  if (pairType === PAIR_USERS_OPTIONS.Dms) return "DMS";
  return "";
};

export const PairUsers = ({ pairType, onClose }: PairUsersProps) => {
  const { t } = useTranslation();

  const [pairedUsers, setPairedUsers] = useState<PairedUserState[]>([]);
  const [newPairedUser, setNewPairedUser] = useState<PairedUserState>({ user_id: null, pair_to: null });

  const {
    filters: { dealer_location_ids }
  } = useGetUsersDashboardFilters();
  const { users } = useUsersQuery();
  const { data: dmsUsers, refetch: refetchDmsUsers } = useDmsUsersQuery(dealer_location_ids[0] ?? 0);
  const { data: planItMechanics, refetch: refetchPlanItMechanics } = usePlanItMechanicsQuery(dealer_location_ids);
  const updatePairOneIUsers = useUpdatePairOneIUsersMutation(pairedUsers);
  const updatePairPlanItUsers = useUpdatePairPlanItUsersMutation(pairedUsers);
  const updatePairDmsUsers = useUpdatePairDmsUsersMutation(pairedUsers);
  const deletePairedUsers = useDeletePairedUsersMutation();
  const { data: oneIPlanningMechanics, refetch: refetchOneIPlanningMechanics } = useOneIPlanningMechanicsQuery(dealer_location_ids);

  useEffect(() => {
    if (pairType) {
      if (pairType === PAIR_USERS_OPTIONS.OneIAutomotive) refetchOneIPlanningMechanics();
      if (pairType === PAIR_USERS_OPTIONS.PlanIt) refetchPlanItMechanics();
      if (pairType === PAIR_USERS_OPTIONS.Dms) refetchDmsUsers();
    }
  }, [pairType]);

  const getPairField = () => {
    if (pairType === PAIR_USERS_OPTIONS.OneIAutomotive) return "onei_planning_monteur_id";
    if (pairType === PAIR_USERS_OPTIONS.PlanIt) return "planit_planning_mechanic_id";
    return "dms_nr";
  };

  const getPreparedOptions = () => {
    if (pairType === PAIR_USERS_OPTIONS.OneIAutomotive) {
      const oneIOptions =
        oneIPlanningMechanics?.map(element => ({
          text: `${element.onei_monteur_name} - ${element.onei_code}`,
          value: element.onei_planning_monteur_id,
          key: element.onei_planning_monteur_id,
          disabled: !!element.onei_user_id
        })) || [];

      return oneIOptions;
    }
    if (pairType === PAIR_USERS_OPTIONS.PlanIt) {
      const planItOptions =
        planItMechanics?.map(element => ({
          text: `${element.planit_mechanic_name} - ${element.planit_code}`,
          value: element.planit_planning_mechanic_id,
          key: element.planit_planning_mechanic_id,
          disabled: !!element.planit_user_id
        })) || [];

      return planItOptions;
    }

    return dmsUsers?.map(element => ({ value: element.dms_nr, text: element.name, disabled: !!element.user_id })) || [];
  };

  const handleAddPairOption = () => {
    if (!newPairedUser.user_id || !newPairedUser.pair_to) return;

    setPairedUsers([...pairedUsers, { ...newPairedUser }]);
    setNewPairedUser({ user_id: null, pair_to: null });
  };

  const handleClose = () => {
    onClose();
    setPairedUsers([]);
    setNewPairedUser({ user_id: null, pair_to: null });
  };

  const handleDeleteLocallyPairedUser = (userIdx: number) => setPairedUsers(pairedUsers.filter((_user, idx) => idx !== userIdx));
  const handleDeletePairedUser = async (user: User, pairField: string) => {
    await deletePairedUsers.mutateAsync({ ...user, [pairField]: null });
  };

  const handleSave = async () => {
    if (pairType === PAIR_USERS_OPTIONS.OneIAutomotive) await updatePairOneIUsers.mutateAsync();
    if (pairType === PAIR_USERS_OPTIONS.PlanIt) await updatePairPlanItUsers.mutateAsync();
    if (pairType === PAIR_USERS_OPTIONS.Dms) await updatePairDmsUsers.mutateAsync();

    handleClose();
  };

  const pairField = getPairField();
  const preparedOptions = getPreparedOptions();
  const usersToPair: DropdownItemProps[] = users.map(u => ({ key: u.id, value: u.id, text: `${u.first_name} ${u.last_name}` }));

  const mechanics = users
    .filter(user => user.role_id === ROLES.Mechanic && user.dealer_location_id === dealer_location_ids[0])
    .map(u => ({
      key: u.id,
      value: u.id,
      text: `${u.first_name} ${u.last_name}`,
      planit_user_id: u.planit_planning_mechanic_id,
      onei_user_id: u.onei_planning_monteur_id
    }));

  const getAvailablePairingOptions = () => {
    if (pairType === PAIR_USERS_OPTIONS.Dms) return usersToPair.filter(user => !pairedUsers.some(item => item.user_id === user.value));

    return mechanics.filter(
      mechanic =>
        (pairType === PAIR_USERS_OPTIONS.OneIAutomotive ? !mechanic.planit_user_id : !mechanic.onei_user_id) && !pairedUsers.some(item => item.user_id === mechanic.value)
    );
  };

  return (
    <Modal open={!!pairType} size="small" onClose={handleClose} className="PairUsers" closeIcon>
      <Modal.Header>
        {t("v8_pair_users", "Pair Users")} {getPairUsersTitle(pairType)}
      </Modal.Header>

      <Modal.Content scrolling>
        {users.map((user, index) => {
          if (!preparedOptions.some(po => po.value === user[pairField])) return null;

          const options = users.map(user => ({ key: user.id, value: user.id, text: `${user.first_name} ${user.last_name}` }));

          return (
            <div className="paired-options-container" key={index}>
              <Button icon="trash" onClick={() => handleDeletePairedUser(user, pairField)} color="red" />

              <div className="paired-options-container-dropdowns">
                <Dropdown options={options} value={user.id} selection disabled fluid />
                <Dropdown options={preparedOptions} value={user[pairField] ?? ""} selection disabled fluid />
              </div>

              <Icon name="checkmark" color="green" />
            </div>
          );
        })}

        {pairedUsers.map((user, idx) => (
          <div className="paired-options-container" key={idx}>
            <Button icon="trash" onClick={() => handleDeleteLocallyPairedUser(idx)} color="red" />

            <div className="paired-options-container-dropdowns">
              <Dropdown options={pairType === PAIR_USERS_OPTIONS.Dms ? usersToPair : mechanics} value={user.user_id ?? ""} selection disabled fluid />
              <Dropdown options={preparedOptions} value={user.pair_to ?? ""} selection disabled fluid />
            </div>

            <Icon name="checkmark" color="green" />
          </div>
        ))}

        <div className="add-new-paired-option">
          <div className="paired-options-container-dropdowns">
            <Dropdown
              options={getAvailablePairingOptions()}
              noResultsMessage={t("v8_no_options_available", "No options available")}
              name="user_id"
              placeholder={t("v8_select_user", "Select User")}
              value={newPairedUser.user_id ?? ""}
              onChange={(_e, data) => setNewPairedUser({ ...newPairedUser, [data.name]: data.value })}
              selectOnBlur={false}
              clearable
              selection
              fluid
              search
            />

            <Dropdown
              options={preparedOptions.filter(option => !pairedUsers.some(user => user.pair_to === option.value))}
              noResultsMessage={t("v8_no_options_available", "No options available")}
              name="pair_to"
              placeholder={t("v8_pair_to", "Pair to")}
              value={newPairedUser.pair_to ?? ""}
              onChange={(_e, data) => setNewPairedUser({ ...newPairedUser, [data.name]: data.value })}
              selectOnBlur={false}
              clearable
              selection
              fluid
              search
            />
          </div>

          <Button icon onClick={handleAddPairOption} color="green" disabled={!newPairedUser.user_id || !newPairedUser.pair_to}>
            <Icon name="plus" />
          </Button>
        </div>
      </Modal.Content>

      <Modal.Actions>
        <div className="action-buttons-container">
          <Button className="light" type="button" onClick={handleClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>

          <Button disabled={!pairedUsers.length} type="button" onClick={handleSave} color="green">
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
